import { dates, formats, validators } from 'investira.sdk';

/** Resolve o valor principal do ponto conforme o modo de exibição do indicador. */
export function getIndicadorDisplayValue(pPoint = {}, pDisplayConfig = {}) {
  const xValueKeys = [pDisplayConfig.valueKey || 'cotacao', pDisplayConfig.fallbackValueKey];

  for (const xValueKey of xValueKeys) {
    if (!xValueKey || validators.isNull(pPoint[xValueKey])) {
      continue;
    }

    const xValue = Number(pPoint[xValueKey]);

    if (Number.isFinite(xValue)) {
      return xValue;
    }
  }

  return null;
}

/** Formata um valor conforme a precisão e a unidade do modo de exibição. */
export function formatIndicadorDisplayValue(pValue, pDisplayConfig = {}, pIsVariation = false) {
  if (validators.isNull(pValue)) {
    return '---';
  }

  const xDecimals = pIsVariation ? 2 : pDisplayConfig.decimals || 2;
  const xSuffix = pIsVariation
    ? pDisplayConfig.variationSuffix || '%'
    : pDisplayConfig.suffix || '';

  return `${formats.formatNumber(pValue, xDecimals, true, false)}${xSuffix}`;
}

/** Converte o histórico para pontos consumidos pelo gráfico. */
export function formatIndicadorCotacaoData(pData = [], pDisplayConfig = {}) {
  return pData.map(pPoint => {
    return {
      x: dates.toDate(pPoint.data).getTime(),
      y: getIndicadorDisplayValue(pPoint, pDisplayConfig),
      metadata: { ...pPoint }
    };
  });
}

/** Calcula a mudança entre dois valores conforme o modo de exibição. */
function pvCalculateVariation(pInitialValue, pCurrentValue, pVariationMode = 'percentage') {
  if (!Number.isFinite(pInitialValue) || !Number.isFinite(pCurrentValue)) {
    return null;
  }

  if (pVariationMode === 'points') {
    return pCurrentValue - pInitialValue;
  }

  if (pInitialValue === 0) {
    return null;
  }

  return (pCurrentValue / pInitialValue - 1) * 100;
}

/** Resume valor atual, extremos e mudanças a partir do histórico recebido. */
export function getIndicadorCotacaoResumo(pData = [], pDisplayConfig = {}) {
  const xData = pData
    .filter(pPoint => {
      const xDate = dates.toDate(pPoint.data);
      const xValue = getIndicadorDisplayValue(pPoint, pDisplayConfig);

      return !validators.isNull(xValue) && !Number.isNaN(xDate.getTime());
    })
    .map(pPoint => ({
      data: pPoint.data,
      value: getIndicadorDisplayValue(pPoint, pDisplayConfig)
    }))
    .sort((pFirst, pSecond) => dates.toDate(pFirst.data) - dates.toDate(pSecond.data));

  if (!xData.length) {
    return null;
  }

  const xFirst = xData[0];
  const xCurrent = xData[xData.length - 1];
  const xTwelveMonthsAgo = dates.toDate(xCurrent.data);

  xTwelveMonthsAgo.setFullYear(xTwelveMonthsAgo.getFullYear() - 1);

  const xLastTwelveMonths = xData.filter(
    pPoint => dates.toDate(pPoint.data).getTime() >= xTwelveMonthsAgo.getTime()
  );
  const xFirstTwelveMonths = xLastTwelveMonths[0];
  const xTwelveMonthsValues = xLastTwelveMonths.map(pPoint => pPoint.value);

  return {
    atual: xCurrent.value,
    dataAtual: xCurrent.data,
    minimo12m: Math.min(...xTwelveMonthsValues),
    maximo12m: Math.max(...xTwelveMonthsValues),
    variacao12m: pvCalculateVariation(
      xFirstTwelveMonths.value,
      xCurrent.value,
      pDisplayConfig.variationMode
    ),
    variacaoInicio: pvCalculateVariation(xFirst.value, xCurrent.value, pDisplayConfig.variationMode)
  };
}
